import React, { useState } from "react";
import { DEFAULT_DATE_TIME_PATTERN } from "utils/constants";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { Dialog } from "primereact/dialog";
import { I, T } from "services/lang/LanguageService";
import { Tooltip } from "primereact/tooltip";
import { formatDateTime } from "utils/time-utils";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import { DiffType, LastValueIndicatorProps } from "./LastValueIndicatorTypes";
import "./LastValueIndicator.scss";

export default function LastValueIndicator(props: LastValueIndicatorProps) {
  const [dialog, setDialog] = useState<boolean>(false);

  const { data } = useGetRecentData([props.valueDataset], {
    count: 2,
    options: {
      refetchInterval: (props.updateFrequency ?? 5) * 1000,
    },
  });
  const decimalPlaces = data?.[0].decimalPlaces ?? 2;
  const diffDecimalPlaces = props.diffValueDecimalPlaces ?? decimalPlaces;
  const currentData = data?.[0].data;

  const getDiff = () => {
    if (!currentData) return 0;

    if (props.diffType === DiffType.PERCENTAGE) {
      return `${(((currentData[1].value - currentData[0].value) / currentData[0].value) * 100).toFixed(diffDecimalPlaces)}%`;
    }

    return `${(currentData[1].value - currentData[0].value).toFixed(diffDecimalPlaces)} ${data[0].unit}`;
  };

  const renderDiffTooltipValueRow = (
    label: string,
    value: number,
    unit: string,
    timestamp: string
  ) => (
    <div className="flex flex-row justify-content-between gap-2">
      <b>{label}:</b>
      <span>
        {value.toFixed(diffDecimalPlaces)} {unit},{" "}
        {formatDateTime(timestamp, DEFAULT_DATE_TIME_PATTERN)}
      </span>
    </div>
  );

  return (
    <div className="last-value-indicator flex flex-column">
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-column gap-2">
          <div className="title font-semibold">{I(props.title)}</div>
          <div className="subtitle font-medium">{I(props.subtitle)}</div>
        </div>
        {props.displayLastValueDiff &&
          currentData &&
          (() => {
            const prev = currentData[0]?.value;
            const curr = currentData[1]?.value;
            const isNegative = curr < prev;
            const diffClass = isNegative ? "negative" : "positive";
            const arrowDirection = isNegative ? "down" : "up";

            return (
              <div
                id="last-value-diff-tooltip"
                className={`last-value-diff w-full flex flex-row align-items-center justify-content-end font-bold ${
                  props.useDiffIndicatorColors ? "indicator-colors" : ""
                }`}
              >
                <div className={diffClass}>{getDiff()}</div>
                <i className={`pi pi-arrow-${arrowDirection} ${diffClass}`} />
              </div>
            );
          })()}
      </div>
      <div
        className={`value-container flex flex-row gap-2 align-items-end ${props.highlightValue ? "highlight-value" : ""}`}
        onDoubleClick={() => setDialog(true)}
      >
        <div className="value cursor-pointer last-value-tooltip font-bold	">
          {currentData?.[1].value.toFixed(decimalPlaces)}
        </div>
        <div className="unit cursor-pointer last-value-tooltip">{data?.[0].unit} </div>
      </div>
      <Tooltip target="#last-value-diff-tooltip" mouseTrack mouseTrackLeft={10}>
        {currentData?.[0] && currentData?.[1] && data ? (
          <div className="flex flex-column gap-1">
            {renderDiffTooltipValueRow(
              T("word.previous-value"),
              currentData?.[0].value,
              data[0].unit,
              currentData?.[0].key
            )}
            {renderDiffTooltipValueRow(
              T("word.current-value"),
              currentData?.[1].value,
              data[0].unit,
              currentData?.[1].key
            )}
          </div>
        ) : (
          T("word.no-data")
        )}
      </Tooltip>
      <Tooltip target=".last-value-tooltip" mouseTrack mouseTrackLeft={10}>
        {currentData?.[0] && currentData?.[1] && data ? (
          <div>
            {renderDiffTooltipValueRow(
              T("word.current-value"),
              currentData?.[1].value,
              data[0].unit,
              currentData?.[1].key
            )}
          </div>
        ) : (
          T("word.no-data")
        )}
      </Tooltip>

      <Dialog
        header={T("label.scada-tags-chart")}
        visible={dialog}
        style={{ width: "min(100vw, 1000px)", height: "500px" }}
        modal={false}
        onHide={() => {
          setDialog(false);
        }}
      >
        <DatasetLineChart
          title=""
          identifiers={[props.valueDataset]}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </div>
  );
}
